import type { Cacheable } from '@/types'
import { Calculator, type DataFile } from '@precoa/calculator-sdk'
import { captureException } from '@sentry/vue'
import localforageLib from 'localforage'
import { defineStore } from 'pinia'
import { computed, ref, toRaw } from 'vue'

const localforage = localforageLib.createInstance({
	name: 'calculator',
})

export const useCalculatorStore = defineStore('calculator', () => {
	const dataFile = ref<DataFile>()
	const etag = ref('')
	let calculatorInstance: Calculator | null = null

	/** Attempt to get an updated data file from Gitlab */
	async function downloadDataFile() {
		const response = await fetch(
			'https://gitlab.com/api/v4/projects/47483787/jobs/artifacts/main/raw/calculator-data.json?job=publish_data_file',
			{
				headers: {
					'If-None-Match': etag.value,
					'PRIVATE-TOKEN': import.meta.env.VITE_CALC_SDK_GITLAB_TOKEN,
				},
			}
		)

		try {
			if (response.ok) {
				const result = await response.json()
				const data = result as DataFile

				dataFile.value = data
				calculatorInstance = null

				await saveDataFileToStorage({
					data,
					etag: response.headers.get('etag') || '',
				})
			} else if (response.status === 304) {
				// Not modified. Nice!
			} else if (response.status >= 400) {
				captureException(new Error('Error downloading data file'), {
					level: 'warning',
					extra: ((await response.json().catch(() => {})) as any) || {},
				})
			}
		} catch (e) {
			console.error(e)
			captureException(e)
		}
	}

	/** Get an instance of the rate calculator with the most recent data available */
	function getCalculator() {
		if (!calculatorInstance) {
			if (dataFile.value) {
				calculatorInstance = new Calculator(dataFile.value)
			} else {
				calculatorInstance = new Calculator()
			}
		}
		return calculatorInstance
	}

	function saveDataFileToStorage(payload: Cacheable<DataFile>) {
		return localforage.setItem<Cacheable<DataFile>>('dataFile', payload)
	}

	async function getDataFileFromStorage() {
		const record =
			(await localforage.getItem<Cacheable<DataFile>>('dataFile')) ?? undefined

		if (record) {
			dataFile.value = record.data
			etag.value = record.etag
			calculatorInstance = null
		}
	}

	return {
		dataFile,
		downloadDataFile,
		getCalculator,
		getDataFileFromStorage,
	}
})
